import React, { useEffect, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

export interface LookupListItem {
    value: string;
    display: string;
}

interface ProjectSelectionDialogProps {
    open: boolean;
    title?: string;
    apiUrl: string;
    token: string;
    onClose: () => void;
    onSelectProject: (project: LookupListItem) => void;
    emptyMessage?: string;
}

const ProjectSelectionDialog: React.FC<ProjectSelectionDialogProps> = ({
    open,
    title = 'Select Project',
    apiUrl,
    token,
    onClose,
    onSelectProject,
    emptyMessage = 'No projects available.',
}) => {
    const [projects, setProjects] = useState<LookupListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) {
            return;
        }

        let isActive = true;

        setLoading(true);
        setError('');

        fetch(`${apiUrl}/api/TMI/GetProjectsForDropDown`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (!isActive) {
                    return;
                }

                if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setProjects(res.results);
                    return;
                }

                const message = res.callStatusMessage || 'Unable to load projects.';
                setError(message);
                setProjects([]);
            })
            .catch(err => {
                if (!isActive) {
                    return;
                }

                setError(`Error loading projects: ${err.toString()}`);
                setProjects([]);
            })
            .finally(() => {
                if (isActive) {
                    setLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [apiUrl, open, token]);

    if (!open) {
        return null;
    }

    return (
        <Dialog title={title} onClose={onClose} width={420}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px 0', minWidth: '320px' }}>
                {error && (
                    <div style={{ color: '#b71c1c', backgroundColor: '#ffebee', borderRadius: '4px', padding: '8px 10px' }}>
                        {error}
                    </div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <span className="k-icon k-font-icon k-i-loading" style={{ fontSize: '28px' }} />
                        <div style={{ marginTop: '8px' }}>Loading projects...</div>
                    </div>
                ) : projects.length === 0 ? (
                    <p style={{ margin: 0, color: '#666' }}>{emptyMessage}</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto' }}>
                        {projects.map(project => (
                            <button
                                key={project.value}
                                type="button"
                                className="k-button k-button-md k-rounded-md"
                                style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                                onClick={() => onSelectProject(project)}
                            >
                                {project.display}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <DialogActionsBar>
                <button type="button" className="k-button k-button-md k-rounded-md" onClick={onClose}>
                    Cancel
                </button>
            </DialogActionsBar>
        </Dialog>
    );
};

export default ProjectSelectionDialog;
